import React from "react";
import { X, Loader2 } from "lucide-react";
import { LEAVE_TYPES } from "../../constants/hrConstants";
import { SearchableSelect } from "@/components/ui";

export function LeaveApplyModal({ isOpen, onClose, employees, formData, formErrors, onFieldChange, onSubmit, isSaving }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-[15px] font-bold text-slate-800">Apply for Leave</h2>
            <p className="text-[12px] text-slate-400 mt-0.5">Submit a leave request</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Employee */}
          <div>
            <SearchableSelect
              label="Employee *"
              placeholder="Search employee..."
              options={employees.map(e => ({ value: e.id, label: `${e.name} (${e.id})`, ...e }))}
              value={formData.employeeId}
              onChange={(val) => {
                const emp = employees.find(e => e.id === val);
                onFieldChange("employeeId", val);
                onFieldChange("employeeName", emp?.name || "");
              }}
              error={formErrors.employeeId}
            />
          </div>

          {/* Leave Type */}
          <div>
            <label className="label">Leave Type *</label>
            <select
              className={`input ${formErrors.leaveType ? "border-red-400" : ""}`}
              value={formData.leaveType || ""}
              onChange={(e) => onFieldChange("leaveType", e.target.value)}
            >
              <option value="">Select type</option>
              {LEAVE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            {formErrors.leaveType && <p className="text-red-500 text-[11px] mt-1">{formErrors.leaveType}</p>}
          </div>

          {/* Duration Type Dropdown */}
          <div>
            <label className="label">Duration *</label>
            <select
              className="input"
              value={formData.durationType || "long_leave"}
              onChange={(e) => onFieldChange("durationType", e.target.value)}
            >
              <option value="long_leave">Long Leave</option>
              <option value="half_day">Half Day</option>
            </select>
          </div>

          {/* Dates */}
          {formData.durationType === "half_day" ? (
            <div>
              <label className="label">Date *</label>
              <input
                type="date" className={`input ${formErrors.fromDate ? "border-red-400" : ""}`}
                value={formData.fromDate || ""}
                onChange={(e) => onFieldChange("fromDate", e.target.value)}
                min={(() => {
                  const d = new Date();
                  const year = d.getFullYear();
                  const month = String(d.getMonth() + 1).padStart(2, "0");
                  const day = String(d.getDate()).padStart(2, "0");
                  return `${year}-${month}-${day}`;
                })()}
                max={(() => {
                  const d = new Date();
                  const year = d.getFullYear();
                  const month = String(d.getMonth() + 1).padStart(2, "0");
                  const day = String(d.getDate()).padStart(2, "0");
                  return `${year}-${month}-${day}`;
                })()}
              />
              {formErrors.fromDate && <p className="text-red-500 text-[11px] mt-1">{formErrors.fromDate}</p>}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">From Date *</label>
                <input
                  type="date" className={`input ${formErrors.fromDate ? "border-red-400" : ""}`}
                  value={formData.fromDate || ""} onChange={(e) => onFieldChange("fromDate", e.target.value)}
                />
                {formErrors.fromDate && <p className="text-red-500 text-[11px] mt-1">{formErrors.fromDate}</p>}
              </div>
              <div>
                <label className="label">To Date *</label>
                <input
                  type="date" className={`input ${formErrors.toDate ? "border-red-400" : ""}`}
                  value={formData.toDate || ""} onChange={(e) => onFieldChange("toDate", e.target.value)}
                  min={formData.fromDate || ""}
                />
                {formErrors.toDate && <p className="text-red-500 text-[11px] mt-1">{formErrors.toDate}</p>}
              </div>
            </div>
          )}

          {/* Days badge */}
          {formData.days > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2 text-center">
              <span className="text-[13px] font-semibold text-[#2b448f]">{formData.days} day{formData.days > 1 ? "s" : ""}</span>
              <span className="text-[12px] text-slate-500 ml-1">of leave requested</span>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="label">Reason *</label>
            <textarea
              className={`input h-20 py-2 resize-none ${formErrors.reason ? "border-red-400" : ""}`}
              placeholder="Reason for leave..."
              value={formData.reason || ""} onChange={(e) => onFieldChange("reason", e.target.value)}
            />
            {formErrors.reason && <p className="text-red-500 text-[11px] mt-1">{formErrors.reason}</p>}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary" disabled={isSaving}>Cancel</button>
          <button onClick={onSubmit} className="btn-primary min-w-[120px]" disabled={isSaving}>
            {isSaving ? (
              <span className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Submitting...</span>
            ) : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}
